import React, { createContext, useContext, useReducer, useMemo, useCallback, useState } from 'react';

// Начальное состояние оборудования
const getInitialState = () => ({
  selectedPump: null,
  selectedFilter: null,
  selectedSand: [],
  selectedHeater: null,
  selectedSpecificHeater: null,
  selectedChlorinator: null,
  selectedUltraviolet: null,
  selectedProjector: null,
  selectedTransformer: null,
  lightingNotNeeded: null,
  selectedEmbedded: null,
  selectedLadder: null,
  selectedPVC: null,
  selectedMosaic: null,
  disinfectionNotNeeded: null,
  selectedSkimmer: null,
  selectedNozzle: null,
  selectedDrain: null,
  selectedGrate: null,
  savedSkimmerSelection: null,
  savedGrateSelection: null,
  selectedConsole: null,
  selectedHeatPump: null,
  selectedHeatingType: null,
  heatingNotNeeded: null,
  ladderNotNeeded: null,

  // Общий список для 3D и сметы
  selectedEquipment: [],

  // Состояние навигации
  navigationState: {
    originalStep: null,
    cancelledStepsStack: [],
    stepContext: {}
  },

  // Флаги UI
  showPVCSelection: false,
  showMosaicSelection: false,
  lastRemovedFinishingType: null,
});

// Типы действий
const ActionTypes = {
  SET_EQUIPMENT_STATE: 'SET_EQUIPMENT_STATE',
  RESET_EQUIPMENT: 'RESET_EQUIPMENT',
  SELECT_ITEM: 'SELECT_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_NAVIGATION: 'UPDATE_NAVIGATION',
  SET_CONSOLE: 'SET_CONSOLE'
};

// Редьюсер
function equipmentReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_EQUIPMENT_STATE:
      if (typeof action.payload === 'function') {
        const newState = action.payload(state);
        return { ...state, ...newState };
      }
      return { ...state, ...action.payload };

    case ActionTypes.RESET_EQUIPMENT:
      return getInitialState();

    default:
      return state;
  }
}

const EquipmentContext = createContext();

export function EquipmentProvider({ children }) {
  const [state, dispatch] = useReducer(equipmentReducer, null, getInitialState);

  /**
   * Только для 3D при шаге онбординга «сцена»: демо-насос/консоль/песок не попадают в reducer
   * и не трогают правую панель подбора оборудования.
   * @type {[null | { demoPump: object; items: object[] }, function]}
   */
  const [onboarding3dSceneOverlay, setOnboarding3dSceneOverlayState] = useState(null);

  const setOnboarding3dSceneOverlay = useCallback((next) => {
    setOnboarding3dSceneOverlayState((prev) => {
      if (next === null) {
        return prev === null ? prev : null;
      }
      if (prev && next?.demoPump?.id === prev.demoPump?.id && next.items?.length === prev.items?.length) {
        return prev;
      }
      return next;
    });
  }, []);

  // Стабильные колбэки
  const setEquipment = useCallback(
    (payload) => dispatch({ type: ActionTypes.SET_EQUIPMENT_STATE, payload }),
    []
  );
  const resetEquipment = useCallback(() => {
    setOnboarding3dSceneOverlay(null);
    dispatch({ type: ActionTypes.RESET_EQUIPMENT });
  }, []);

  // Мемоизируем value
  const value = useMemo(() => ({
    ...state,
    state,
    dispatch,

    // Хелперы для частых действий
    setEquipment,
    resetEquipment,
    onboarding3dSceneOverlay,
    setOnboarding3dSceneOverlay,
  }), [state, setEquipment, resetEquipment, setOnboarding3dSceneOverlay, onboarding3dSceneOverlay]);

  return (
    <EquipmentContext.Provider value={value}>
      {children}
    </EquipmentContext.Provider>
  );
}

export function useEquipment() {
  const context = useContext(EquipmentContext);
  if (!context) {
    throw new Error('useEquipment must be used within EquipmentProvider');
  }
  return context;
}
